/*!
 * @license
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { BaseApi } from './base.api';
import { throwIfNotDefined } from '../../../assert';
import { buildCollectionParam } from '../../../alfrescoApiClient';
import { CategoryEntry, CategoryPaging } from '../model';
export class CategoriesApi extends BaseApi {
    getSubcategories(categoryId, opts) {
        throwIfNotDefined(categoryId, 'categoryId');
        const pathParams = {
            categoryId
        };
        const queryParams = {
            skipCount: opts === null || opts === void 0 ? void 0 : opts.skipCount,
            maxItems: opts === null || opts === void 0 ? void 0 : opts.maxItems,
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv'),
            include: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.include, 'csv')
        };
        return this.get({
            path: '/categories/{categoryId}/subcategories',
            pathParams,
            queryParams,
            returnType: CategoryPaging
        });
    }
    getCategory(categoryId, opts) {
        throwIfNotDefined(categoryId, 'categoryId');
        const pathParams = {
            categoryId
        };
        const queryParams = {
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv'),
            include: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.include, 'csv')
        };
        return this.get({
            path: '/categories/{categoryId}',
            pathParams,
            queryParams,
            returnType: CategoryEntry
        });
    }
    getCategoryLinksForNode(nodeId, opts) {
        throwIfNotDefined(nodeId, 'nodeId');
        const pathParams = {
            nodeId
        };
        const queryParams = {
            skipCount: opts === null || opts === void 0 ? void 0 : opts.skipCount,
            maxItems: opts === null || opts === void 0 ? void 0 : opts.maxItems,
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv'),
            include: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.include, 'csv')
        };
        return this.get({
            path: '/nodes/{nodeId}/category-links',
            pathParams,
            queryParams,
            returnType: CategoryPaging
        });
    }
    deleteCategory(categoryId) {
        throwIfNotDefined(categoryId, 'categoryId');
        const pathParams = {
            categoryId
        };
        return this.delete({
            path: '/categories/{categoryId}',
            pathParams
        });
    }
    unlinkNodeFromCategory(nodeId, categoryId) {
        throwIfNotDefined(nodeId, 'nodeId');
        throwIfNotDefined(categoryId, 'categoryId');
        const pathParams = {
            nodeId,
            categoryId
        };
        return this.delete({
            path: '/nodes/{nodeId}/category-links/{categoryId}',
            pathParams
        });
    }
    updateCategory(categoryId, categoryBodyUpdate, opts) {
        throwIfNotDefined(categoryId, 'categoryId');
        throwIfNotDefined(categoryBodyUpdate, 'categoryBodyUpdate');
        const pathParams = {
            categoryId
        };
        const queryParams = {
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv'),
            include: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.include, 'csv')
        };
        return this.put({
            path: '/categories/{categoryId}',
            pathParams,
            queryParams,
            bodyParam: categoryBodyUpdate,
            returnType: CategoryEntry
        });
    }
    createSubcategories(categoryId, categoryBodyCreate, opts) {
        throwIfNotDefined(categoryId, 'categoryId');
        throwIfNotDefined(categoryBodyCreate, 'categoryBodyCreate');
        const pathParams = {
            categoryId
        };
        const queryParams = {
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv'),
            include: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.include, 'csv')
        };
        return this.post({
            path: '/categories/{categoryId}/subcategories',
            pathParams,
            queryParams,
            bodyParam: categoryBodyCreate,
            returnType: CategoryEntry
        });
    }
    linkNodeToCategory(nodeId, categoryLinkBodyCreate, opts) {
        throwIfNotDefined(nodeId, 'nodeId');
        throwIfNotDefined(categoryLinkBodyCreate, 'categoryLinkBodyCreate');
        const pathParams = {
            nodeId
        };
        const queryParams = {
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv'),
            include: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.include, 'csv')
        };
        return this.post({
            path: '/nodes/{nodeId}/category-links',
            pathParams,
            queryParams,
            bodyParam: categoryLinkBodyCreate,
            returnType: CategoryEntry
        });
    }
}
